import { getBondChatSocket } from "./bondchat/generalChatSingleton";
import { requireNetwork } from "../../utils/requireNetwork";

const OFFLINE_MSG = "You're offline. Connect to the internet to continue.";

const waitForSocket = (timeoutMs = 15000) =>
  new Promise((resolve, reject) => {
    const socket = getBondChatSocket();
    if (socket?.connected) {
      resolve(socket);
      return;
    }
    if (!socket) {
      reject(new Error("Connection not ready. Please wait a moment and try again."));
      return;
    }
    const timer = setTimeout(() => {
      reject(new Error("Connection timed out. Check your network and try again."));
    }, timeoutMs);
    const onConnect = () => {
      clearTimeout(timer);
      socket.off("connect", onConnect);
      resolve(socket);
    };
    socket.on("connect", onConnect);
    try {
      socket.connect();
    } catch {
      clearTimeout(timer);
      socket.off("connect", onConnect);
      reject(new Error("Could not connect to the server."));
    }
  });

const parseSocketFailure = (payload) => {
  if (!payload) return "Failed to create prediction.";
  if (typeof payload === "string") return payload;
  return (
    (payload.error && String(payload.error).trim()) ||
    (payload.message && String(payload.message).trim()) ||
    "Failed to create prediction."
  );
};

export const createPredictionWithAck = async (payload, timeoutMs = 45000) => {
  const {
    token,
    fixtureId,
    outcomes = [],
    bettingAdvice,
    isPremium = false,
    price = 0,
  } = payload || {};

  if (!token) {
    throw new Error("Authentication required.");
  }
  if (!requireNetwork(OFFLINE_MSG)) {
    throw new Error(OFFLINE_MSG);
  }
  if (!fixtureId) {
    throw new Error("Missing fixture.");
  }
  if (!Array.isArray(outcomes) || !outcomes.length) {
    throw new Error("Add at least one outcome.");
  }
  if (!String(bettingAdvice || "").trim()) {
    throw new Error("Betting advice is required.");
  }

  const normalized = {
    token,
    fixtureId: String(fixtureId),
    outcomes: outcomes.map((o) => {
      const market = o?.market;
      const hasMarket =
        market &&
        typeof market === "object" &&
        market.section &&
        market.subSection &&
        market.key;

      return {
        ...(hasMarket
          ? {
              market: {
                section: String(market.section),
                subSection: String(market.subSection),
                key: String(market.key),
              },
            }
          : {}),
        confidence: Number(o.confidence) || 0,
        analysis: String(o.analysis || "").trim(),
      };
    }),
    bettingAdvice: String(bettingAdvice).trim(),
    isPremium: Boolean(isPremium),
    price: isPremium ? Number(price) || 0 : 0,
  };

  const missingMarket = normalized.outcomes.some((o) => !o.market);
  if (missingMarket) {
    throw new Error("Each pick must use a supported market outcome.");
  }

  const socket = await waitForSocket(timeoutMs);
  console.log("[PREDICT][SOCKET] Socket ready for createPrediction", {
    socketId: socket.id,
    connected: socket.connected,
    fixtureId: normalized.fixtureId,
    outcomesCount: normalized.outcomes.length,
    isPremium: normalized.isPremium,
    price: normalized.price,
  });

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (fn) => (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    const resolveOk = finish(resolve);
    const rejectErr = finish((err) =>
      reject(err instanceof Error ? err : new Error(parseSocketFailure(err)))
    );

    const cleanup = () => {
      clearTimeout(timer);
      socket.off("predictionCreated", onEventSuccess);
      socket.off("predictionError", onEventError);
    };

    const timer = setTimeout(() => {
      console.error("[PREDICT][SOCKET] createPrediction timed out", {
        fixtureId: normalized.fixtureId,
        timeoutMs,
      });
      rejectErr(new Error("Request timed out. Check your connection and try again."));
    }, timeoutMs);

    const onEventSuccess = (response) => {
      if (response?.prediction || response?.message) {
        console.log("[PREDICT][SOCKET] predictionCreated event", {
          predictionId: response?.prediction?._id,
          message: response?.message,
        });
        resolveOk(response);
      }
    };

    const onEventError = (errPayload) => {
      console.error("[PREDICT][SOCKET] predictionError event", errPayload);
      rejectErr(new Error(parseSocketFailure(errPayload)));
    };

    socket.once("predictionCreated", onEventSuccess);
    socket.once("predictionError", onEventError);

    try {
      console.log("[PREDICT][SOCKET] Emitting createPrediction", {
        fixtureId: normalized.fixtureId,
        outcomesCount: normalized.outcomes.length,
        isPremium: normalized.isPremium,
        price: normalized.price,
      });
      socket.timeout(timeoutMs).emit("createPrediction", normalized, (err, ...ackArgs) => {
        if (settled) return;
        if (err) {
          // Ack timed out — fall back to predictionCreated / predictionError events.
          console.warn("[PREDICT][SOCKET] createPrediction ack error", err);
          if (err.message === "operation has timed out") return;
          rejectErr(new Error(err.message || "Failed to create prediction."));
          return;
        }
        const response =
          ackArgs.find((arg) => arg?.prediction || arg?.message || arg?.error) || ackArgs[0];
        console.log("[PREDICT][SOCKET] createPrediction ack", response);
        if (response?.error) {
          rejectErr(new Error(parseSocketFailure(response)));
          return;
        }
        if (response?.prediction || response?.message) {
          resolveOk(response);
        }
      });
    } catch (emitErr) {
      console.error("[PREDICT][SOCKET] createPrediction emit threw", emitErr);
      rejectErr(
        emitErr instanceof Error ? emitErr : new Error("Could not send prediction to server.")
      );
    }
  });
};

/** Unlock a premium community tip via the same socket event as mobile. */
export const unlockCommunityPredictionWithAck = async (
  { predictionId, userId, token },
  timeoutMs = 30000
) => {
  if (!token) throw new Error("Authentication required.");
  if (!predictionId) throw new Error("Missing prediction.");
  if (!userId) throw new Error("Missing user.");
  if (!requireNetwork(OFFLINE_MSG)) throw new Error(OFFLINE_MSG);

  const socket = await waitForSocket(timeoutMs);
  const targetId = String(predictionId);

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (fn) => (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    const resolveOk = finish(resolve);
    const rejectErr = finish((err) =>
      reject(err instanceof Error ? err : new Error(parseSocketFailure(err)))
    );

    const cleanup = () => {
      clearTimeout(timer);
      socket.off("predictionUnlocked", onUnlocked);
      socket.off("predictionError", onError);
    };

    const timer = setTimeout(() => {
      rejectErr(new Error("Unlock timed out. Check your connection and try again."));
    }, timeoutMs);

    const onUnlocked = (response) => {
      if (String(response?.predictionId || "") === targetId) {
        resolveOk(response);
      }
    };

    const onError = (errPayload) => {
      // Ignore unrelated prediction errors while waiting for this unlock.
      const msg = parseSocketFailure(errPayload);
      if (/view/i.test(msg) && /not found/i.test(msg)) return;
      rejectErr(new Error(msg || "Failed to unlock prediction."));
    };

    socket.on("predictionUnlocked", onUnlocked);
    socket.on("predictionError", onError);

    const payload = {
      predictionId: targetId,
      userId: String(userId),
      token,
    };

    try {
      socket.timeout(timeoutMs).emit("unlockPrediction", payload, (err, ...ackArgs) => {
        if (settled) return;
        if (err) {
          if (err.message === "operation has timed out") return;
          rejectErr(new Error(err.message || "Failed to unlock prediction."));
          return;
        }
        const response =
          ackArgs.find((arg) => arg?.predictionId || arg?.error || arg?.message) || ackArgs[0];
        if (response?.error) {
          rejectErr(new Error(parseSocketFailure(response)));
          return;
        }
        if (response?.predictionId) {
          resolveOk(response);
        }
      });
    } catch (emitErr) {
      rejectErr(
        emitErr instanceof Error ? emitErr : new Error("Could not send unlock request.")
      );
    }
  });
};
